import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import {
    matchesStreamErrorCode as matchesStreamCode,
    parseStreamError,
    type StreamErrorPayload,
} from 'models'

export type { StreamErrorPayload }

type ErrorCode =
    | 'context_length_exceeded'
    | 'entity-not-found'
    | 'rate-limit-exceeded'
    | 'origin-not-allowed'
    | 'credit-exhausted'
    | 'conversation-blocked'
    | 'bad request'
    | 'internal-server-error'
    | 'gateway-disabled'
    | 'model-unavailable'

function matchesCode(error: Error | undefined, code: ErrorCode): boolean {
    return error?.message?.toLowerCase().includes(code) ?? false
}

export function getStreamErrorPayload(
    error: Error | undefined,
): StreamErrorPayload | undefined {
    return parseStreamError(error?.message)?.payload
}

export function useChatbotError(error: MaybeRefOrGetter<Error | undefined>) {
    const isContextWindowError = computed(() =>
        matchesCode(toValue(error), 'context_length_exceeded'),
    )
    const isNotFoundError = computed(() =>
        matchesCode(toValue(error), 'entity-not-found'),
    )
    const isRateLimitError = computed(() =>
        matchesCode(toValue(error), 'rate-limit-exceeded'),
    )
    const isOriginNotAllowedError = computed(() =>
        matchesCode(toValue(error), 'origin-not-allowed'),
    )
    const isCreditExhaustedError = computed(() =>
        matchesCode(toValue(error), 'credit-exhausted'),
    )
    const isConversationBlockedError = computed(() =>
        matchesCode(toValue(error), 'conversation-blocked'),
    )
    const isBadRequestError = computed(() =>
        matchesCode(toValue(error), 'bad request'),
    )
    const isInternalServerError = computed(() =>
        matchesCode(toValue(error), 'internal-server-error'),
    )
    const isGatewayConfigError = computed(() =>
        matchesStreamCode(toValue(error)?.message, 'GATEWAY_CONFIG'),
    )
    /**
     * The organization does not use the platform gateway and this agent's
     * model is one of its. A decision, not a hiccup: retrying in ten seconds
     * gets the same answer.
     */
    const isGatewayDisabledError = computed(() =>
        matchesCode(toValue(error), 'gateway-disabled'),
    )
    /**
     * The agent's model is on the organization's endpoint, which is off, gone
     * or no longer serves it. It lasts until somebody changes the endpoint or
     * the agent, so it is not retried either.
     */
    const isModelUnavailableError = computed(() =>
        matchesCode(toValue(error), 'model-unavailable'),
    )

    /** Errors that trigger an automatic retry countdown */
    const isRefreshableError = computed(
        () =>
            !isContextWindowError.value &&
            !isNotFoundError.value &&
            !isOriginNotAllowedError.value &&
            !isCreditExhaustedError.value &&
            !isConversationBlockedError.value &&
            !isBadRequestError.value &&
            !isInternalServerError.value &&
            !isGatewayConfigError.value &&
            !isGatewayDisabledError.value &&
            !isModelUnavailableError.value,
    )

    return {
        isContextWindowError,
        isNotFoundError,
        isRateLimitError,
        isOriginNotAllowedError,
        isCreditExhaustedError,
        isConversationBlockedError,
        isBadRequestError,
        isInternalServerError,
        isGatewayConfigError,
        isGatewayDisabledError,
        isModelUnavailableError,
        isRefreshableError,
    }
}
